import { openPopupMenu, type PopupMenuElement } from '@kerfjs/ui/popup-menu';

import { materializedRowMenus } from '../data/state.js';

/**
 * Lazy line-row action menus (HS2-D2G57R). A closed row menu renders its trigger
 * with no entries; activating that trigger fills the row's menu in, then opens
 * it from its new trigger. Rows keep their filled menu for the rest of the
 * demand, so focus returns to a live trigger when the menu closes.
 */

/** Mark a row's menu as filled in for `demandId`; switching demands starts a new set. */
export function materializeRowMenu(demandId: string, lineId: string): void {
  const current = materializedRowMenus.value;
  if (current.demandId === demandId && current.lineIds.has(lineId)) return;
  const lineIds = new Set(current.demandId === demandId ? current.lineIds : []);
  lineIds.add(lineId);
  materializedRowMenus.value = { demandId, lineIds };
}

/** The lazy menu whose trigger `event` activates, if any. */
function lazyMenuFor(event: Event): HTMLElement | null {
  const target = event.target;
  if (!(target instanceof Element)) return null;
  const trigger = target.closest('.kui-popup-menu__trigger');
  const menu = trigger?.closest('.kui-popup-menu');
  return menu instanceof HTMLElement && menu.hasAttribute('data-row-menu-lazy') ? menu : null;
}

const OPEN_KEYS = new Set(['Enter', ' ', 'ArrowDown', 'ArrowUp']);

/** Wire lazy row menus under the page-lifetime `root`; returns a disposer. */
export function wireLazyRowMenus(root: HTMLElement, frame = requestAnimationFrame): () => void {
  const activate = (event: Event): void => {
    if (event instanceof KeyboardEvent && !OPEN_KEYS.has(event.key)) return;
    const menu = lazyMenuFor(event);
    if (menu === null) return;
    // Keep Kerf from opening the empty menu and the row from taking the click.
    event.preventDefault();
    event.stopPropagation();
    const lineId = menu.dataset.rowMenuLazy ?? '';
    const demandId = menu.dataset.rowMenuDemand ?? '';
    if (lineId === '' || demandId === '') return;
    materializeRowMenu(demandId, lineId);
    let frames = 0;
    const open = (): void => {
      const row = root.querySelector(`[data-line-item="${CSS.escape(lineId)}"]`);
      const filled = row?.querySelector<PopupMenuElement>('[data-row-menu]:not([data-row-menu-lazy])');
      if (filled !== null && filled !== undefined) {
        filled.querySelector<HTMLElement>('.kui-popup-menu__trigger')?.focus();
        openPopupMenu(filled);
      } else if (++frames < 30) frame(open);
    };
    frame(open);
  };
  root.addEventListener('click', activate, { capture: true });
  root.addEventListener('keydown', activate, { capture: true });
  return () => {
    root.removeEventListener('click', activate, { capture: true });
    root.removeEventListener('keydown', activate, { capture: true });
  };
}
